import { Link } from 'react-router-dom'
import { Card, DataState, PageHeader, Stat } from '@/components/Common'
import { useFetch } from '@/lib/hooks'
import type { ConfigCheck, QueryLogSummary, SystemStatus } from '@/lib/api'

/**
 * The page somebody lands on, answering one question: is anything wrong that I
 * would not otherwise notice?
 *
 * Almost every failure this system has is invisible from the page that caused
 * it. A worklist nothing serves, a rule that can never fire, a device with no
 * facility on a multi-site installation — each of those looks fine on its own
 * page and presents at the modality as an empty list with no error. So the
 * checks come first, above the counts, and the counts are context rather than
 * the point.
 */
export function Overview() {
  const status = useFetch<SystemStatus>('/api/v1/system/status', 15000)
  const checks = useFetch<ConfigCheck[]>('/api/v1/system/checks', 60000)
  const traffic = useFetch<QueryLogSummary>('/api/v1/query-log/summary?hours=24', 60000)

  const errors = (checks.data ?? []).filter((c) => c.level === 'error')
  const warnings = (checks.data ?? []).filter((c) => c.level === 'warning')
  const counts = status.data?.counts ?? {}

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle="What this worklist server is configured to do, and anything that would stop it."
      />

      {status.data && !status.data.store.reachable && (
        <div className="notice notice-error">
          <strong>MwlConfig is unreachable.</strong> {status.data.store.error}
          <p className="hint">
            The daemon is unaffected — it keeps serving from its last good
            snapshot — so modalities are still being answered. Only this console
            is degraded.
          </p>
        </div>
      )}

      <Card title="Configuration checks">
        <DataState
          loading={checks.loading}
          error={checks.error}
          empty={(checks.data ?? []).length === 0}
          emptyMessage="Nothing to flag. Every rule points at a live worklist, every source can enforce its bounds, and every device is attributable."
        />
        {errors.map((check, i) => (
          <CheckRow key={`e${i}`} check={check} />
        ))}
        {warnings.map((check, i) => (
          <CheckRow key={`w${i}`} check={check} />
        ))}
      </Card>

      <Card title="Traffic — last 24 hours">
        <DataState
          loading={traffic.loading}
          error={traffic.error}
          empty={false}
          emptyMessage=""
        />
        {traffic.data && (
          <>
            <div className="stat-row">
              <Stat label="Queries" value={traffic.data.queries.toLocaleString()} />
              <Stat
                label="No rule matched"
                value={traffic.data.no_rule.toLocaleString()}
                tone={traffic.data.no_rule > 0 ? 'error' : 'ok'}
                hint="a device served an empty list it did not expect"
              />
              <Stat
                label="Empty results"
                value={traffic.data.empty.toLocaleString()}
                tone={traffic.data.empty > 0 ? 'warn' : 'ok'}
                hint="matched a rule, found no work"
              />
              <Stat
                label="Failed"
                value={traffic.data.failed.toLocaleString()}
                tone={traffic.data.failed > 0 ? 'error' : 'ok'}
              />
              <Stat
                label="Median-ish"
                value={traffic.data.avg_ms != null ? `${traffic.data.avg_ms} ms` : '—'}
                hint={`slowest ${traffic.data.max_ms} ms`}
              />
            </div>
            {traffic.data.no_rule > 0 && (
              <div className="notice notice-warn">
                Some queries matched no rule at all. That is the commonest cause
                of "the worklist is empty" — the caller is authorised and simply
                has no rule covering it.{' '}
                <Link to="/query-log">See which devices</Link>.
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="What is configured">
        <div className="stat-row">
          <Stat label="Facilities" value={counts.facilities ?? '—'} />
          <Stat label="Departments" value={counts.departments ?? '—'} />
          <Stat label="Devices" value={counts.devices ?? '—'} />
          <Stat label="Listeners" value={counts.listeners ?? '—'} />
          <Stat label="Worklists" value={counts.worklists ?? '—'} />
          <Stat label="Rules" value={counts.rules ?? '—'} />
          <Stat label="Sources" value={counts.sources ?? '—'} />
          <Stat
            label="Scheduled steps"
            value={counts.items?.toLocaleString() ?? '—'}
            hint="active, all dates"
          />
        </div>
        <p className="card-note">
          Changes on any page here reach the daemon within about two seconds. No
          restart, and a change to a rule or a worklist interrupts nothing —
          only a change to a listener's port or timeouts makes that one listener
          rebind.
        </p>
      </Card>

      <Card title="Where to start">
        <ul className="detail-list">
          <li>
            <Link to="/simulator">Rule simulator</Link> — pick a device and see
            exactly which worklists it would be served and why each rule did or
            did not fire. It runs the daemon's own rule engine, so the answer is
            the one a modality gets.
          </li>
          <li>
            <Link to="/query-log">Query log</Link> — what every modality has
            actually asked for, and what it got.
          </li>
          <li>
            <Link to="/rules">Rules</Link> — in evaluation order, which is the
            order that decides what happens.
          </li>
        </ul>
      </Card>
    </>
  )
}

function CheckRow({ check }: { check: ConfigCheck }) {
  return (
    <div className={check.level === 'error' ? 'notice notice-error' : 'notice notice-warn'}>
      <strong>{check.title}</strong>
      <div>{check.detail}</div>
      {check.where && (
        <div className="hint">
          <Link to={`/${check.where}`}>Go to {check.where.replace('-', ' ')}</Link>
        </div>
      )}
    </div>
  )
}
